import { escapeMarkup, isWatchType, productURL } from './watch-buying-contract';
import { getGroupKey, getGroupTitle, getInternalTitle, isInStock } from './title-swatch-utils';
import { isProtectedCardProduct } from '../../../app/Libraries/ProductCardAvailability';

export const atelierWatchEligible = (hit: any): boolean => !!hit && isWatchType(hit.product_type)
  && !!getGroupTitle(hit) && !isProtectedCardProduct(hit);

const atelierSiblingCache = new Map<string, any[]>();

/** Separate lookup/cache leaves the live legacy colour-switching groups intact. */
export async function fetchAtelierWatchGroups(hits: any[], search: (queries: any[]) => Promise<any>, indexName: string): Promise<Map<string, any[]>> {
  const eligible = hits.filter(atelierWatchEligible);
  const groups = new Map<string, any[]>();
  const unique = new Map<string, any>();
  eligible.forEach(hit => unique.set(getGroupKey(hit), hit));
  const uncached = [...unique.entries()].filter(([key]) => !atelierSiblingCache.has(indexName + ':' + key));
  if (uncached.length) {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const attributesToRetrieve = ['objectID', 'title', 'handle', 'image', 'meta', 'tags', 'named_tags', 'vendor', 'inventory_available', 'inventory_quantity', 'product_type', 'price', 'compare_at_price', 'option_names', 'sku'];
      const request = search(uncached.map(([key, hit]) => ({ indexName, query: key.startsWith('m:') ? '' : String(hit.title || ''), params: {
        hitsPerPage: 50, attributesToRetrieve, filters: 'NOT tags:"inquiry-only"', analytics: false, clickAnalytics: false,
        ...(key.startsWith('m:') ? { facetFilters: [[`meta.custom.title_internal:${getInternalTitle(hit)}`]] } : { restrictSearchableAttributes: ['title'] }),
      } })));
      const response = await Promise.race([request, new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Related watches timed out')), 3000); })]);
      if (!Array.isArray((response as any)?.results)) throw new Error('Related watches unavailable');
      uncached.forEach(([key], position) => {
        const records = (response as any).results[position]?.hits;
        if (Array.isArray(records)) atelierSiblingCache.set(indexName + ':' + key, records.filter(item => atelierWatchEligible(item) && getGroupKey(item) === key && isInStock(item)));
      });
    } catch { /* Navigation is optional: fall back to matching current-page watches. */ }
    finally { if (timeout !== undefined) clearTimeout(timeout); }
  }
  unique.forEach((_, key) => groups.set(key, atelierSiblingCache.get(indexName + ':' + key) || eligible.filter(item => getGroupKey(item) === key && isInStock(item))));
  return groups;
}


/** Related colourways navigate to their own product; card purchase actions stay exact. */
export function nativeWatchCardOptions(hit: any, siblings: any[], locale: string, thumbnail: (image: string) => string, previewAttributes: (item: any, url: string) => string = () => ''): string {
  const brand = (item: any) => String(item?.named_tags?.brand || item?.vendor || '').trim().toLowerCase();
  const currentBrand = brand(hit);
  const empty = '<div class="rf-watch-card-options rf-watch-card-options--empty" aria-hidden="true"></div>';
  if (!currentBrand || !atelierWatchEligible(hit)) return empty;
  const seen = new Set<string>();
  const options = [hit, ...siblings].filter(item => {
    if (!atelierWatchEligible(item) || brand(item) !== currentBrand
      || getGroupKey(item) !== getGroupKey(hit) || !item.image || seen.has(item.handle)
      || item.tags?.includes('inquiry-only')
      || (item.handle !== hit.handle && !item.inventory_available)
      || !/^\d+$/.test(String(item.objectID || '').replace(/^gid:\/\/shopify\/ProductVariant\//, ''))
      || !productURL(item.handle, item.objectID, locale)) return false;
    try { if (new URL(item.image).protocol !== 'https:') return false; } catch { return false; }
    seen.add(item.handle);
    return true;
  });
  if (options.length < 2 || options[0] !== hit) return empty;
  const label = locale === 'fr' ? 'Options de cadran et de bracelet' : 'Dial & bracelet options';
  const links = options.map((item, index) => {
    const selected = item.handle === hit.handle;
    const name = `${locale === 'fr' ? 'Voir la montre' : 'View watch'} ${index + 1}: ${item.title || hit.title}`;
    const url = new URL(productURL(item.handle, item.objectID, locale), 'https://native.invalid');
    // Explicit locale wins over an older language cookie on private staging.
    url.searchParams.set('lang', locale === 'fr' ? 'fr' : 'en');
    return `<a class="rf-watch-card-options__link" href="${escapeMarkup(url.pathname + url.search)}" aria-label="${escapeMarkup(name)}"${selected ? ' aria-current="true"' : ''}${previewAttributes(item, url.pathname + url.search)}><img src="${escapeMarkup(thumbnail(item.image))}" alt="" loading="lazy" decoding="async" /></a>`;
  }).join('');
  return `<div class="rf-watch-card-options" role="group" aria-label="${escapeMarkup(label)}"><div class="rf-watch-card-options__track">${links}</div></div>`;
}
